import { SplitFactory } from "@splitsoftware/splitio"

// Configuration
const DEFAULT_USER_KEY = "anonymous"
const SDK_READY_TIMEOUT = 1.5 // 1.5 seconds
const CHATBOT_FEATURE_FLAG_NAME = "help-center-chatbot-toggle"

// Types
interface SplitClientManager {
  client: SplitIO.IBrowserClient | null
  isReady: boolean
  factory: SplitIO.IBrowserSDK | null
}

// Client management
let defaultClientManager: SplitClientManager = {
  client: null,
  isReady: false,
  factory: null
}

/**
 * Initialize Split.io client with proper error handling
 */
const initializeSplitClient = async (): Promise<SplitIO.IBrowserClient> => {
  if (defaultClientManager.client && defaultClientManager.isReady) {
    return defaultClientManager.client
  }

  const GATSBY_SPLIT_IO_API_KEY = process.env.GATSBY_SPLIT_IO_API_KEY
  if (!GATSBY_SPLIT_IO_API_KEY) {
    process.env.GATSBY_DEBUG === "true" &&
      console.error("GATSBY_SPLIT_IO_API_KEY environment variable is not set")
    throw new Error("GATSBY_SPLIT_IO_API_KEY environment variable is not set")
  }

  return new Promise((resolve, reject) => {
    try {
      const factory: SplitIO.IBrowserSDK = SplitFactory({
        core: {
          authorizationKey: GATSBY_SPLIT_IO_API_KEY,
          key: DEFAULT_USER_KEY
        },
        startup: {
          readyTimeout: SDK_READY_TIMEOUT
        }
      })

      const client = factory.client()

      client.on(client.Event.SDK_READY, () => {
        defaultClientManager = {
          client,
          isReady: true,
          factory
        }
        process.env.GATSBY_DEBUG === "true" &&
          console.info("Split.io SDK is ready")
        resolve(client)
      })

      client.on(client.Event.SDK_READY_TIMED_OUT, () => {
        process.env.GATSBY_DEBUG === "true" &&
          console.error("Split.io SDK ready timeout")
        reject(new Error("Split.io SDK ready timeout"))
      })

      client.on(client.Event.SDK_UPDATE, () => {
        process.env.GATSBY_DEBUG === "true" &&
          console.info("Split.io SDK updated")
      })
    } catch (error) {
      process.env.GATSBY_DEBUG === "true" &&
        console.error("Error initializing Split.io client:", error)
      reject(error)
    }
  })
}

/**
 * Get feature flag treatment with proper error handling
 */
export async function getFeatureFlagTreatment(
  featureFlagName: string,
  userKey?: string,
  attributes?: SplitIO.Attributes
): Promise<SplitIO.Treatment> {
  try {
    const splitClient = await initializeSplitClient()

    // For different user keys, we update the client's key rather than creating new clients
    if (userKey && userKey !== DEFAULT_USER_KEY) {
      // Note: Split.io doesn't support changing user key on existing client
      // This is a limitation that should be documented
      process.env.GATSBY_DEBUG === "true" &&
        console.warn(
          `Custom user key '${userKey}' provided, but client uses default key. Consider using attributes for user identification.`
        )
    }

    const treatment = splitClient.getTreatment(featureFlagName, attributes)
    process.env.GATSBY_DEBUG === "true" &&
      console.info(`Treatment for ${featureFlagName}: ${treatment}`)
    return treatment
  } catch (error) {
    process.env.GATSBY_DEBUG === "true" &&
      console.error(`Error fetching feature flag ${featureFlagName}:`, error)
    return "control" // Return control as fallback
  }
}

/**
 * Check if help-center-chatbot-toggle feature flag is enabled
 */
export async function getChatbotTreatment(userKey?: string): Promise<boolean> {
  const treatment = await getFeatureFlagTreatment(
    CHATBOT_FEATURE_FLAG_NAME,
    userKey,
    {
      splitAnonymousId: [getSplitAnonymoudId() || ""]
    }
  )
  return treatment === "on"
}

const getSplitAnonymoudId = (): string | null => {
  if (typeof window !== "undefined" && window.localStorage) {
    const splitAnonymousId = localStorage.getItem("splitAnonymousId")
    return splitAnonymousId
  }
  return null
}
